// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Zod schemas + wire types for the SFDC drift-resolution endpoints
 * under /v1/crm/sfdc/drift/{entityType}/{entityKey}/...
 */
import { responseMetadataSchema } from '@/api/types';
import { sparsePersonSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * SFDC drift endpoints are path-generic over entity type. Widen the
 * union when a new Hardfin entity is wired in the backend dispatcher
 * (src/modules/sfdc/driftresolution/dispatch.go).
 */
export const sfdcDriftEntityTypeSchema = z.enum([
  'location',
  'company',
  'project',
]);
export type SFDCDriftEntityType = z.infer<typeof sfdcDriftEntityTypeSchema>;

/**
 * `kind` discriminates how the FE renders a row:
 *  - `scalar` — `sfdcValue` / `hardfinValue` are user-readable
 *    strings; render them directly.
 *  - `reference` — both sides are opaque SFDC Account ids. Render
 *    `sfdcDisplay` / `hardfinDisplay` when available, and surface a
 *    "Sync pending" placeholder + disabled Pull when the SFDC-side
 *    cached name hasn't propagated yet.
 */
const fieldDiscrepancyKindSchema = z.enum(['scalar', 'reference']);

/**
 * One Hardfin↔SFDC discrepancy row surfaced to the resolution popover.
 *
 * `hardfinDisplay` and `sfdcDisplay` are friendly names that the
 * resolution UI renders in place of the raw `hardfinValue` /
 * `sfdcValue` when populated. Used for reference-type fields whose
 * raw value is an opaque SFDC Account id (Operator, Site); unset for
 * scalar fields, where the value itself is already user-readable.
 */
const fieldDiscrepancySchema = z.object({
  fieldName: z.string(),
  label: z.string(),
  kind: fieldDiscrepancyKindSchema,
  hardfinValue: z.string().nullable(),
  sfdcValue: z.string().nullable(),
  hardfinDisplay: z.string().nullable(),
  sfdcDisplay: z.string().nullable(),
});
export type FieldDiscrepancy = z.infer<typeof fieldDiscrepancySchema>;

/**
 * A dismissed-discrepancy row carries the original FieldDiscrepancy
 * payload plus the dismissal audit: when, and which person did it.
 * `dismissedBy` is nullable to cover rows whose audit user was lost
 * (deactivated, FK orphan, or rows created before the audit columns).
 */
const dismissedFieldDiscrepancySchema = fieldDiscrepancySchema.extend({
  dismissedAt: dateTimeSchema.nullable(),
  dismissedBy: sparsePersonSchema.nullable(),
});

/**
 * Body for GET /v1/crm/sfdc/drift/{entityType}/{entityKey}/diff.
 * `fields` is the actionable drift; `dismissed` is rows the operator
 * has previously accepted and backs the popover's "Dismissed records"
 * collapsible section.
 */
export const sfdcDriftDiffResponseSchema = z.object({
  data: z.object({
    fields: z.array(fieldDiscrepancySchema),
    dismissed: z.array(dismissedFieldDiscrepancySchema),
  }),
  metadata: responseMetadataSchema,
});
export type SFDCDriftDiffResponse = z.infer<typeof sfdcDriftDiffResponseSchema>;

/**
 * Body for both Pull and Dismiss endpoints. Carries the post-action
 * needs_review state so the indicator flips without a refetch, plus
 * the entity's current Hardfin display name — the post-pull toast
 * renders this so the link text reflects the freshly-written value
 * rather than the stale name captured when the popover opened.
 */
export const sfdcDriftResolutionResponseSchema = z.object({
  data: z.object({
    needsReview: z.boolean(),
    displayName: z.string(),
  }),
  metadata: responseMetadataSchema,
});
export type SFDCDriftResolutionResponse = z.infer<
  typeof sfdcDriftResolutionResponseSchema
>;
